import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import Avatar from '../../profile/ui/Avatar';

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;
const MAX_PEOPLE = 5;

/**
 * People matching the home search, from the org-wide directory.
 * Rows open /people?q=<name>, not /profile/:id: member profiles are gated by
 * canViewMember and most employees cannot open them.
 * A failed lookup renders nothing -- tool search keeps working. While the
 * first lookup for a term is in flight (no rows yet) a quiet "Searching
 * people…" status shows, so the section doesn't just pop in late.
 */
export default function PeopleResults({ term }) {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const requestSeq = useRef(0);
  const q = (term || '').trim();

  useEffect(() => {
    // Bump on every term change, including one too short to search, so a
    // response still in flight for an older term is always discarded.
    const seq = ++requestSeq.current;
    if (q.length < MIN_CHARS) { setPeople([]); setLoading(false); return undefined; }
    const timer = setTimeout(() => {
      setLoading(true);
      axios.get(`${API_BASE_URL}/api/directory`, { withCredentials: true, params: { q, limit: MAX_PEOPLE } })
        .then((res) => {
          if (seq !== requestSeq.current) return; // a newer term has been typed
          const rows = Array.isArray(res.data?.employees) ? res.data.employees : [];
          setPeople(rows.slice(0, MAX_PEOPLE));
          setLoading(false);
        })
        .catch(() => { if (seq === requestSeq.current) { setPeople([]); setLoading(false); } });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [q]);

  if (people.length === 0) {
    return loading ? <p className="home-muted" role="status">Searching people…</p> : null;
  }

  return (
    <section className="home-section" aria-labelledby="home-people-h">
      <div className="home-section__head">
        <h2 id="home-people-h" className="home-section__title">People</h2>
        <Link className="home-link" to={`/people?q=${encodeURIComponent(q)}`}>See all in the directory</Link>
      </div>
      <ul className="home-people">
        {people.map((p) => (
          <li key={p.key}>
            <Link className="home-person" to={`/people?q=${encodeURIComponent(p.name)}`}>
              <Avatar name={p.name} src={p.photo} size="node" variant="hashed" />
              <span className="home-person__text">
                <span className="home-person__name">{p.name}</span>
                {p.odoo_job_title && <span className="home-person__title">{p.odoo_job_title}</span>}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
